<template>
  <div
    class="as-incident-list as-d-flex as-flex-column"
    :class="[
      {
        'as-scrolling as-scrolling__result-popup': !isHomePage,
        'as-home-scrollable-content': isHomePage,
        'as-incident-list-sm': screenWidth < 375,
      },
      `as-incident-list-${siteName} as-parent-incident as-parent-incident-${siteName}`,
    ]"
  >
    <div class="as-incident-list__content" v-if="processedIncidents.length">
      <div class="as-incident-list__content-data">
        <div
          v-for="(incident, index) in processedIncidents"
          :key="index"
          class="as-incident-list__event as-d-flex as-align-items-center"
        >
          <div
            class="as-incident-list__event-time as-d-flex as-align-items-center"
          >
            <div>
              {{ incident.displayTime ? incident.displayTime + "'" : "" }}
            </div>
          </div>
          <div
            class="as-incident-list__event-detail as-d-flex as-flex-1"
            :class="getEventClass(incident)"
          >
            <div class="as-incident-list__event-score as-d-flex as-flex-1">
              <button v-if="shouldShowScore(incident)">
                <span class="as-score-home">{{
                  incident.home_score || 0
                }}</span>
                :
                <span class="as-score-away">{{
                  incident.away_score || 0
                }}</span>
              </button>
            </div>
            <div
              class="as-incident-list__event-icon as-d-flex justify-content-center as-align-items-center"
            >
              <div
                v-if="incident.type && [11, 12, 26, 27].includes(incident.type)"
                class="as-incident-list__midfield d-flex"
                :class="{
                  'as-incident-list__midfield-away':
                    incident.home_score < incident.away_score,
                }"
              >
                <span>{{ formatTitle(incident.type) }}</span
                ><span>{{ incident.home_score || 0 }}</span
                ><span>:</span>
                <span>{{ incident.away_score || 0 }}</span>
              </div>

              <div
                v-if="
                  incident.type && ![11, 12, 26, 27].includes(incident.type)
                "
                class="as-icon"
              >
                <svg
                  aria-hidden="true"
                  :type="incident.type.toString()"
                  :width="iconMap[incident.type]?.width"
                  :height="iconMap[incident.type]?.height"
                  class="as-incident-list__icon"
                >
                  <use :href="iconMap[incident.type]?.icon"></use>
                </svg>
              </div>
            </div>
            <div class="as-incident-list__player as-d-flex as-flex-1">
              <div
                class="as-incident-list__player-info as-d-flex as-align-items-center"
              >
                <div
                  v-if="
                    incident.type &&
                    [1, 8, 7, 29, 30, 28, 17, 16, 3, 4].includes(incident.type)
                  "
                  class="as-incident-list__player-logo as-cursor-pointer"
                >
                  <img
                    class="as-rounded-pill as-incident-list__player-logo--in as-player-avatar as-bg-white"
                    :src="getPlayerImageUrl(incident)"
                    :alt="incident.player?.name"
                  />
                </div>
                <div
                  v-if="incident.type === 9 && incident.playerIn"
                  class="as-cursor-pointer as-player-in"
                  :class="{
                    'as-player-in': incident.playerIn
                  }"
                >
                  <img
                    :src="getPlayerImageUrl(incident, 'in')"
                    class="as-rounded-pill as-incident-list__player-logo--in as-player-avatar as-bg-white"
                    :alt="incident.playerIn.name"
                  />
                </div>
                <div v-if="incident.type === 9 && incident.playerOut" class="as-cursor-pointer as-player-out">
                  <img
                    :src="getPlayerImageUrl(incident, 'out')"
                    class="as-rounded-pill as-incident-list__player-logo--out as-player-avatar as-bg-white"
                    :alt="incident.playerOut.name"
                  />
                </div>
              </div>
              <div
                class="as-incident-list__player-name as-d-flex as-flex-column"
              >
                <span>{{ getPlayerName(incident) }}</span>
                <span
                  class="as-incident-list__player-name--sub"
                  v-if="incident.playerOut"
                  >{{
                    incident.playerOut?.short_name || incident.playerOut?.name
                  }}</span
                >
                <span
                  class="as-incident-list__player-name--sub"
                  v-if="incident.assist1"
                  >{{
                    incident.assist1?.short_name || incident.assist1?.name
                  }}</span
                >
              </div>
            </div>
          </div>
        </div>
        <div class="as-incident-list__start">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_406_2175)">
              <path
                d="M7 0C3.14027 0 0 3.14027 0 7C0 10.8597 3.14027 14 7 14C10.8597 14 14 10.8597 14 7C14 3.14027 10.8597 0 7 0ZM7 13.125C3.62262 13.125 0.875004 10.3774 0.875004 7C0.875004 3.62262 3.62262 0.875004 7 0.875004C10.3774 0.875004 13.125 3.62262 13.125 7C13.125 10.3774 10.3774 13.125 7 13.125Z"
                :fill="colorStartIcon"
              />
              <path
                d="M7.4375 2.625H6.5625V7.18114L9.31567 9.93431L9.93433 9.31566L7.4375 6.81884V2.625Z"
                :fill="colorStartIcon"
              />
            </g>
            <defs>
              <clipPath id="clip0_406_2175">
                <rect width="14" height="14" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
      </div>
      <div class="as-note">
        <div
          v-for="(incident, key) in listIncidents"
          :key="key"
          class="as-note-item"
        >
          <component :is="incident.icon" />{{ incident.label }}
        </div>
      </div>
    </div>
    <NoData v-else />
  </div>
</template>
<script setup lang="ts">
import { computed, defineProps, getCurrentInstance } from "vue";
import type { IIncident, IMatch, IIncidentStatistic } from "~src/interfaces";
import NoData from "~src/components/NoData.vue";
import MatchEnum from "~src/enums/MatchEnum";
import { useMatchDetailStore } from "~src/stores/MatchDetailStore";
import { usecommonStore } from "~src/stores/CommonStore";
const commonStore = usecommonStore();
const matchDetailStore = useMatchDetailStore();

interface IProps {
  incidents: IIncident[];
  match: IMatch;
  isHomePage: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  incidents: () => [] as IIncident[], // Providing a function that returns an empty array of IIncident
  match: () => ({} as IMatch), // Providing a function that returns a default IMatch object,
  isHomePage: false,
});

const siteName = computed(() => commonStore.getSiteName);
const screenWidth = computed(() => matchDetailStore.getScreenWidth);

const listIncidents: IIncidentStatistic[] = MatchEnum.INCIDENT_LIST;

const formatTitle = (type: number) => {
  const titles: { [key: number]: string } = {
    11: "HT",
    12: "FT",
    26: "ET",
    27: "PEN",
  };
  return titles[type] || "Unknown";
};

const showIncidentTitle = (type: number) => [11, 12, 26, 27].includes(type);

const shouldDisplayIncident = (
  incident: IIncident,
  index: number,
  match: IMatch
) => {
  return (
    incident.type !== 10 &&
    incident.type !== 99 &&
    index === 0 &&
    match.status_id === 3
  );
};

const shouldShowScore = (incident: IIncident) =>
  incident.type && [1, 8, 7, 29, 17, 16, 30].includes(incident.type);

const getEventClass = (incident: IIncident) => ({
  "as-incident-list__event--home": incident.position === 1,
  "as-incident-list__event--away": incident.position === 2,
});

const getGoalIconBySite = () => {
  switch(siteName.value) {
    case "xoilac365":
    case "90phut":
    case "cakhia":
    case "rakhoi":
    case "mitom":
      return "#icon-goal-blue";
    case "xoilac86":
    case "soco":
      return "#icon-goal-yellow";
    default:
      return "#icon-goal";
  }
};

const colorStartIcon = computed(() => {
  switch(siteName.value) {
    case "xoilac365":
    case "xoilac86":
    case "mitom":
    case "soco":
      return "#FFB43A";
    default:
      return "#60BB46";
  }
})

const iconMap: {
  [key: number]: { icon: string; width: string; height: string };
} = {
  1: {
    icon: getGoalIconBySite(),
    width: "25px",
    height: "25px",
  },
  3: {
    icon: "#icon-yellow-card",
    width: "11px",
    height: "13px",
  },
  4: {
    icon: "#icon-red-card",
    width: "11px",
    height: "13px",
  },
  8: {
    icon: "#icon-Penalty",
    width: "20px",
    height: "20px",
  },
  9: {
    icon: "#icon-substitution",
    width: "14px",
    height: "18px",
  },
  15: {
    icon: "#icon-twoyellow-red",
    width: "20px",
    height: "20px",
  },
  16: {
    icon: "#icon-PenaltySaved",
    width: "20px",
    height: "20px",
  },
  17: {
    icon: "#icon-soccer",
    width: "20px",
    height: "20px",
  },
  21: {
    icon: "#icon-shots-on-target",
    width: "19px",
    height: "14px",
  },
  28: {
    icon: "#icon-var",
    width: "20px",
    height: "13px",
  },
  29: {
    icon: "#icon-Penalty",
    width: "20px",
    height: "20px",
  },
  30: {
    icon: "#icon-PenaltySaved",
    width: "20px",
    height: "20px",
  },
};

const getPlayerName = (incident: IIncident) => {
  if (incident.player) {
    return incident.player.short_name || incident.player.name;
  }
  if (incident.playerIn) {
    return incident.playerIn.short_name || incident.playerIn.name;
  }
  if (incident.playerOut) {
    return incident.playerOut.short_name || incident.playerOut.name;
  }
  return "";
};

const getPlayerImageUrl = (
  incident: IIncident,
  type: "in" | "out" | "" = ""
) => {
  const instance: any = getCurrentInstance();
  switch (type) {
    case "in":
      return incident.playerIn
        ? instance.proxy?.getImgUrlBySport("player", incident.playerIn.id)
        : "";
    case "out":
      return incident.playerOut
        ? instance.proxy?.getImgUrlBySport("player", incident.playerOut.id)
        : "";
    default:
      return instance.proxy?.getImgUrlBySport("player", incident.player ? incident.player.id : 'default');
  }
};

const isShowTimePlus = computed(() => {
  const incidents = props.incidents.filter((incident: IIncident) =>
    [11, 12, 26].includes(incident.type)
  );

  return incidents.length > 0;
});

const processedIncidents = computed(() => {
  let tempHomeScore = 0;
  let tempAwayScore = 0;
  let currentStatus = 0;

  // check lỗi 2 ht
  const incidents: IIncident[] = [];
  for (const incident of props.incidents) {
    if(incident.type === 11) {
      const check = incidents.find((ind: IIncident) => ind.type === 11);
      if(!check) {
        incidents.push(incident);
      }
    } else {
      incidents.push(incident);
    }
  }

  return incidents
    .filter((incident: IIncident) => incident.type !== 19) // Ensuring `incident` is treated as IIncident
    .map((incident: IIncident, index: number) => {
      if (![11, 12, 26, 27].includes(incident.type)) {
        tempHomeScore = incident.home_score || tempHomeScore;
        tempAwayScore = incident.away_score || tempAwayScore;
      } else {
        incident.home_score = tempHomeScore;
        incident.away_score = tempAwayScore;
        currentStatus = incident.type;
      }
      return {
        ...incident,
        displayTime: getDisplayTime(incident, currentStatus),
      };
    })
    .reverse();
});

const getDisplayTime = (
  incident: IIncident,
  currentStatus: number
): string | number => {
  let displayTime: string | number = incident.time;
  let injuryStartTime: number | null = null;
  let extraTime = false;
  switch (incident.type) {
    case 19: // Injury time start
      injuryStartTime = incident.time;
      break;
    case 11: // First half end
    case 12: // Second half end
      injuryStartTime = null;
      extraTime = incident.time >= 105;
      break;
  }

  // fix lỗi hiệp 1 lại hiển thị số phút lớn hơn 45
  if (
    isShowTimePlus.value &&
    ![11, 12, 26].includes(incident.type) &&
    incident.time > 45
  ) {
    // if (currentStatus === 26 && incident.time > 120) {
    //   displayTime = `120 + ${incident.time - 120}`;
    // } else if (incident.time > 105 && incident.time < 108) {
    //   displayTime = `105 + ${incident.time - 105}`;
    // } else
    if (currentStatus === 11 && incident.time > 90) {
      displayTime = `90 + ${incident.time - 90}`;
    } else if (currentStatus === 0 && incident.time > 45) {
      displayTime = `45 + ${incident.time - 45}`;
    }
  }

  if ([11, 12, 26].includes(incident.type)) {
    displayTime = "";
  } else if (injuryStartTime !== null && !extraTime) {
    // Calculate added time if in injury time
    let addedTime = Math.abs(incident.time - (injuryStartTime ?? 0));
    displayTime =
      addedTime === 0
        ? injuryStartTime
        : `${Math.min(injuryStartTime ?? 0, incident.time)}+${addedTime}`;
  }

  return displayTime;
};
</script>

<script lang="ts">
export default {};
</script>
